import React, { useEffect, useCallback, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, Typography, CircularProgress, Paper } from '@mui/material';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Sidebar from './components/common/Sidebar';
import LoadingSpinner from './components/common/LoadingSpinner';
import LoginPage from './components/auth/LoginPage';
import SSOCallback from './components/auth/SSOCallback';
import ApplicationAdminLogin from './components/auth/ApplicationAdminLogin';
import DesktopOnlyMessage from './components/common/DesktopOnlyMessage';
import Dashboard from './pages/Dashboard';
import Users from './pages/Users';
import Groups from './pages/Groups';
import PermissionSets from './pages/PermissionSets';
import CustomerManagedPolicies from './pages/CustomerManagedPolicies';
import PermissionSetAssignments from './pages/PermissionSetAssignments';
import Replication from './pages/Replication';
import ManualOnboarding from './pages/ManualOnboarding';
import AdminManagement from './pages/AdminManagement';
import APITokens from './pages/APITokens';
import AuditLogs from './pages/AuditLogs';
import AccessLogs from './pages/AccessLogs';
import LogExportSettings from './pages/LogExportSettings';
import CustomerManagement from './pages/CustomerManagement';
import CustomerDashboard from './pages/CustomerDashboard';
import CustomerLogin from './pages/CustomerLogin';
import IdentityProviders from './pages/IdentityProviders';
import CustomApplicationsPage from './pages/CustomApplicationsPage';
import SCIMConfiguration from './pages/SCIMConfiguration';
import SaaSConnection from './pages/SaaSConnection';
import AppAdminAnalytics from './pages/AppAdminAnalytics';
import OrgServices from './pages/OrgServices';
import AccountContactDetail from './pages/AccountContactDetail';
import { useApi } from './hooks/useApi';
import apiService from './services/api';
import { MESSAGES, isCustomerSubdomain, getCkAuthApplicationURL, isJITPortal, isOrgServicesPortal, getCustomerIdFromSubdomain, getKeycloakURL } from './utils/constants';
import { isMobileDevice, getDeviceType } from './utils/deviceDetection';
import './App.css';
import DynamicBanner from './components/common/DynamicBanner';
import ReadOnlyBanner from './components/common/ReadOnlyBanner';
import { ConfirmDialogProvider } from './components/common/ConfirmDialog';

// JIT Portal imports
import { JITLayout, JITSSOCallback, JITRealmRedirect } from './components/jit';
import {
  JITDashboard,
  JITRequestForm,
  JITMyRequests,
  JITApprovals,
  JITActiveSessions,
  JITOwnedAccounts,
  JITManageSessions,
  JITRequestHistory,
} from './pages/jit';
import { getJITUser, getPendingApprovals, parseJITToken, removeJITToken } from './services/jitApi';
import { hasMinRole, ROLES } from './utils/roles';

// CloudKeeper Org Services portal imports
import { PortalLayout, PortalSSOCallback } from './components/portal';
import { RequireService } from './components/portal/RequireService';
import { TrailList, CreateTrail, TrailDetail, EditTrail } from './pages/portal/cloudtrail';
import SecurityHubEntry from './pages/portal/securityhub/SecurityHubEntry';
import Overview from './pages/portal/securityhub/Overview';
import CspmFindings from './pages/portal/securityhub/CspmFindings';
import GuardDutyFindings from './pages/portal/securityhub/GuardDutyFindings';
import Configuration from './pages/portal/securityhub/Configuration';
import EnrollmentPage from './pages/portal/securityhub/EnrollmentPage';
import SettingsPage from './pages/portal/securityhub/SettingsPage';
import GuardDutyEntry from './pages/portal/securityhub/GuardDutyEntry';
import GuardDutyEnrollmentPage from './pages/portal/securityhub/GuardDutyEnrollmentPage';
import GuardDutySettingsPage from './pages/portal/securityhub/GuardDutySettingsPage';
import MacieEntry from './pages/portal/securityhub/MacieEntry';
import MacieBuckets from './pages/portal/securityhub/MacieBuckets';
import MacieFindings from './pages/portal/securityhub/MacieFindings';
import MacieEnrollmentPage from './pages/portal/securityhub/MacieEnrollmentPage';
import MacieSettingsPage from './pages/portal/securityhub/MacieSettingsPage';
import InspectorEntry from './pages/portal/securityhub/InspectorEntry';
import InspectorFindings from './pages/portal/securityhub/InspectorFindings';
import InspectorEnrollmentPage from './pages/portal/securityhub/InspectorEnrollmentPage';
import InspectorSettingsPage from './pages/portal/securityhub/InspectorSettingsPage';
import DetectiveEntry from './pages/portal/securityhub/DetectiveEntry';
import DetectiveFindings from './pages/portal/securityhub/DetectiveFindings';
import DetectiveEnrollmentPage from './pages/portal/securityhub/DetectiveEnrollmentPage';
import DetectiveSettingsPage from './pages/portal/securityhub/DetectiveSettingsPage';
import AccessAnalyzerEntry from './pages/portal/securityhub/AccessAnalyzerEntry';
import AccessAnalyzerFindings from './pages/portal/securityhub/AccessAnalyzerFindings';
import AccessAnalyzerEnrollmentPage from './pages/portal/securityhub/AccessAnalyzerEnrollmentPage';
import AccessAnalyzerSettingsPage from './pages/portal/securityhub/AccessAnalyzerSettingsPage';
import ConfigRecorders from './pages/portal/securityhub/ConfigRecorders';
import StandardsList from './pages/portal/securityhub/StandardsList';
import StandardDetail from './pages/portal/securityhub/StandardDetail';
import ControlDetail from './pages/portal/securityhub/ControlDetail';
import ConfigRules from './pages/portal/securityhub/ConfigRules';
import StackSetsEntry from './pages/portal/stacksets/StackSetsEntry';
import StackSetCreate from './pages/portal/stacksets/StackSetCreate';
import StackSetEdit from './pages/portal/stacksets/StackSetEdit';
import StackSetDetail from './pages/portal/stacksets/StackSetDetail';
import { getPortalUser } from './services/portalApi';

// Create Material UI theme - Minimalist Design System
const theme = createTheme({
  palette: {
    primary: {
      main: '#4698D3', // Primary Blue
      light: '#6BB3E0',
      dark: '#3580B8',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#D64794', // Primary Magenta/Pink
      light: '#E06BAD',
      dark: '#B83A7D',
      contrastText: '#FFFFFF',
    },
    background: {
      default: '#F9FBFD',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#253E66', // Navy/Dark Blue for headings
      secondary: '#999999',
      disabled: '#BDBDBD',
    },
    divider: '#E5E5E5',
    error: {
      main: '#E74C3C',
    },
    success: {
      main: '#27AE60',
    },
    warning: {
      main: '#F39C12',
    },
    info: {
      main: '#4698D3',
    },
  },
  typography: {
    fontFamily: [
      'Metropolis',
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
    ].join(','),
    h1: {
      fontFamily: 'Bitter, Georgia, serif',
      fontWeight: 700,
      letterSpacing: '-0.02em',
      color: '#253E66',
    },
    h2: {
      fontFamily: 'Bitter, Georgia, serif',
      fontWeight: 600,
      letterSpacing: '-0.01em',
      color: '#253E66',
    },
    h3: {
      fontFamily: 'Bitter, Georgia, serif',
      fontWeight: 600,
      color: '#253E66',
    },
    h4: {
      fontFamily: 'Bitter, Georgia, serif',
      fontWeight: 600,
      color: '#253E66',
    },
    h5: {
      fontFamily: 'Metropolis, sans-serif',
      fontWeight: 600,
      color: '#253E66',
    },
    h6: {
      fontFamily: 'Metropolis, sans-serif',
      fontWeight: 600,
      color: '#253E66',
    },
    body1: {
      fontWeight: 400,
      color: '#333333',
    },
    body2: {
      fontWeight: 400,
      color: '#666666',
    },
    button: {
      fontWeight: 600,
      textTransform: 'none',
    },
    subtitle1: {
      fontWeight: 300,
      color: '#999999',
    },
    caption: {
      color: '#999999',
    },
  },
  shape: {
    borderRadius: 6, // Smaller, more subtle border radius
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          background: '#F9FBFD',
          minHeight: '100vh',
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          textTransform: 'none',
          fontWeight: 600,
          padding: '10px 24px',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)',
          },
        },
        containedPrimary: {
          backgroundColor: '#4698D3',
          '&:hover': {
            backgroundColor: '#3580B8',
          },
        },
        containedSecondary: {
          backgroundColor: '#D64794',
          '&:hover': {
            backgroundColor: '#B83A7D',
          },
        },
        outlined: {
          borderWidth: '1.5px',
          '&:hover': {
            borderWidth: '1.5px',
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            borderRadius: 6,
            backgroundColor: '#FFFFFF',
            '& fieldset': {
              borderColor: '#E5E5E5',
            },
            '&:hover fieldset': {
              borderColor: '#D9DBDC',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#4698D3',
              borderWidth: '1.5px',
            },
          },
          '& .MuiInputLabel-root': {
            color: '#999999',
            '&.Mui-focused': {
              color: '#4698D3',
            },
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          boxShadow: '0 2px 12px rgba(0, 0, 0, 0.08)',
          border: '1px solid #E5E5E5',
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: '#E5E5E5',
        },
      },
    },
  },
});

// JIT Portal App Component
function JITPortalApp() {
  const [jitUser, setJitUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pendingCount, setPendingCount] = useState(0);
  const [callbackCompleted, setCallbackCompleted] = useState(false);

  // Add jit-portal class to body so CSS can scope mobile-blocking rules to admin only
  useEffect(() => {
    document.body.classList.add('jit-portal');
    return () => { document.body.classList.remove('jit-portal'); };
  }, []);

  useEffect(() => {
    // On SSO callback, skip stale session — fresh auth is coming
    if (window.location.pathname === '/sso-callback') {
      setLoading(false);
      return;
    }

    // Check for existing JIT user on mount
    const user = getJITUser();
    if (user) {
      // Validate token hasn't expired before restoring session.
      // Without this, an expired token triggers API calls that 401,
      // which clears localStorage and redirects to /login — breaking
      // both /r/{realm} and direct navigation.
      const token = localStorage.getItem('jit_token');
      const claims = token ? parseJITToken(token) : null;
      const isValid = claims?.exp && (claims.exp * 1000 > Date.now());

      if (isValid) {
        setJitUser(user);
        if (hasMinRole(user.role, ROLES.APPROVER)) {
          getPendingApprovals()
            .then(res => setPendingCount((res.data || []).length))
            .catch(() => setPendingCount(0));
        }
      } else {
        // Token expired — clear stale data silently
        removeJITToken();
      }
    }
    setLoading(false);
  }, []);

  const handleJITLogin = useCallback((loginData) => {
    setJitUser({
      email: loginData.email,
      customerId: loginData.customerId,
      realm: loginData.realm,
      role: loginData.role || 'viewer',
    });
    if (hasMinRole(loginData.role, ROLES.APPROVER)) {
      getPendingApprovals()
        .then(res => setPendingCount((res.data || []).length))
        .catch(() => setPendingCount(0));
    }
    setCallbackCompleted(true);
    window.history.replaceState({}, '', '/');
  }, []);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: '#F9FBFD',
        }}>
          <LoadingSpinner />
        </Box>
      </ThemeProvider>
    );
  }

  // DEBUG: Log routing decision for JIT Portal
  console.log('[JIT App Debug] pathname:', window.location.pathname, 'jitUser:', !!jitUser, 'callbackCompleted:', callbackCompleted, 'hostname:', window.location.hostname);

  // SSO Callback route for JIT Portal
  if (window.location.pathname === '/sso-callback' && !callbackCompleted) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ToastContainer position="top-right" autoClose={4000} />
        <JITSSOCallback onLogin={handleJITLogin} />
      </ThemeProvider>
    );
  }

  // Realm redirect route for JIT Portal - handles /r/{realm} URLs
  // This must be checked before the default auth redirect
  if (window.location.pathname.startsWith('/r/')) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Routes>
            <Route path="/r/:realm" element={<JITRealmRedirect />} />
          </Routes>
        </Router>
      </ThemeProvider>
    );
  }

  // Show login prompt page if not authenticated (no automatic redirect)
  if (!jitUser) {
    const baseDomain = (window.env?.BASE_DOMAIN || 'localhost:3000').replace(/:\d+$/, '');

    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#F9FBFD',
            padding: 3,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              maxWidth: 480,
              width: '100%',
              p: 5,
              borderRadius: 3,
              textAlign: 'center',
              border: '1px solid #E5E9F2',
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: '#EBF5FB',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
              }}
            >
              <Box
                component="span"
                sx={{
                  fontSize: 40,
                  color: '#4698D3',
                }}
              >
                🔐
              </Box>
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: '#1A1A2E',
                mb: 2,
              }}
            >
              Authentication Required
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#64748B',
                mb: 3,
                lineHeight: 1.6,
              }}
            >
              To access the JIT Access Portal, please start the login flow from your organization's Prism URL.
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#94A3B8',
              }}
            >
              Contact your administrator if you don't know your organization's Prism URL.
            </Typography>
          </Paper>
        </Box>
      </ThemeProvider>
    );
  }

  // JIT Portal authenticated routes
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastContainer position="top-right" autoClose={4000} />
      <Router>
        <JITLayout user={jitUser} pendingCount={pendingCount}>
          <Routes>
            <Route path="/" element={<JITDashboard />} />
            <Route path="/request" element={<JITRequestForm />} />
            <Route path="/my-requests" element={<JITMyRequests />} />
            <Route path="/active-sessions" element={<JITActiveSessions />} />
            {hasMinRole(jitUser?.role, ROLES.APPROVER) && (
              <>
                <Route path="/approvals" element={<JITApprovals />} />
                <Route path="/request-history" element={<JITRequestHistory />} />
                <Route path="/manage-sessions" element={<JITManageSessions />} />
                <Route path="/owned-accounts" element={<JITOwnedAccounts />} />
              </>
            )}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </JITLayout>
      </Router>
    </ThemeProvider>
  );
}

// CloudKeeper Org Services (Portal) App Component
function OrgServicesPortalApp() {
  const [portalUser, setPortalUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [callbackCompleted, setCallbackCompleted] = useState(false);

  useEffect(() => {
    document.body.classList.add('org-services-portal');
    return () => { document.body.classList.remove('org-services-portal'); };
  }, []);

  // Legacy URL redirects. Two prior forms still accepted:
  //   /cloudtrail/...          → /services/cloudtrail/...
  //   /security/<service>/...  → /services/<service>/...
  // SSO callback paths (handled separately below) pass through untouched.
  useEffect(() => {
    const p = window.location.pathname;
    const search = window.location.search;
    const hash = window.location.hash;

    if (p === '/cloudtrail' || p === '/cloudtrail/') {
      window.history.replaceState({}, '', '/services/cloudtrail' + search + hash);
    } else if (p.startsWith('/cloudtrail/') && p !== '/cloudtrail/sso-callback') {
      window.history.replaceState({}, '', '/services' + p + search + hash);
    } else if (p === '/security' || p === '/security/') {
      window.history.replaceState({}, '', '/services' + search + hash);
    } else if (p.startsWith('/security/') && p !== '/security/sso-callback') {
      window.history.replaceState({}, '', '/services' + p.substring('/security'.length) + search + hash);
    }
  }, []);

  useEffect(() => {
    const user = getPortalUser();
    if (user) {
      setPortalUser(user);
    }
    setLoading(false);
  }, []);

  const handlePortalLogin = useCallback((loginData) => {
    setPortalUser({
      email: loginData.email,
      customerId: loginData.customerId,
      realm: loginData.realm,
      services: loginData.services || [],
    });
    setCallbackCompleted(true);
    window.history.replaceState({}, '', '/services');
  }, []);

  if (loading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          backgroundColor: '#F9FBFD',
        }}>
          <LoadingSpinner />
        </Box>
      </ThemeProvider>
    );
  }

  // SSO Callback route — accept new and both legacy paths.
  const pathname = window.location.pathname;
  if (
    (pathname === '/services/sso-callback' ||
      pathname === '/security/sso-callback' ||
      pathname === '/cloudtrail/sso-callback') &&
    !callbackCompleted
  ) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <ToastContainer position="top-right" autoClose={4000} />
        <PortalSSOCallback onLogin={handlePortalLogin} />
      </ThemeProvider>
    );
  }

  // Unauthenticated on a customer subdomain → auto-initiate SAML login via Keycloak
  // with RelayState=portal. Same shape as the JIT realm-redirect flow.
  // Returns to /services/sso-callback with a one-shot portal_sso_token cookie.
  if (!portalUser) {
    const realm = getCustomerIdFromSubdomain();
    if (realm) {
      const keycloakURL = getKeycloakURL();
      const loginURL = `${keycloakURL}/realms/${realm}/protocol/saml/clients/ckauth-admin?RelayState=portal`;
      // Use replace so the "Authentication Required" card never flashes and the
      // back button doesn't return to a dead-end page.
      window.location.replace(loginURL);
      return (
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100vh',
            backgroundColor: '#F9FBFD',
          }}>
            <LoadingSpinner />
          </Box>
        </ThemeProvider>
      );
    }
  }

  // Fallback: dev / base-domain landing with no realm in the URL.
  // Show the static login-prompt card.
  if (!portalUser) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box
          sx={{
            minHeight: '100vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#F9FBFD',
            padding: 3,
          }}
        >
          <Paper
            elevation={0}
            sx={{
              maxWidth: 480,
              width: '100%',
              p: 5,
              borderRadius: 3,
              textAlign: 'center',
              border: '1px solid #E5E9F2',
            }}
          >
            <Box
              sx={{
                width: 80,
                height: 80,
                borderRadius: '50%',
                backgroundColor: '#EBF5FB',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 24px',
              }}
            >
              <Box
                component="span"
                sx={{
                  fontSize: 40,
                  color: '#4698D3',
                }}
              >
                🔐
              </Box>
            </Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 600,
                color: '#1A1A2E',
                mb: 2,
              }}
            >
              Authentication Required
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: '#64748B',
                mb: 3,
                lineHeight: 1.6,
              }}
            >
              To access CloudKeeper Organization Services, please start the login flow from your organization's Prism URL.
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#94A3B8',
              }}
            >
              Contact your administrator if you don't have Organization Services access.
            </Typography>
          </Paper>
        </Box>
      </ThemeProvider>
    );
  }

  // Default landing inside the portal — send to the first service the user has.
  const services = portalUser.services || [];
  const landing = services.includes('cloudtrail')
    ? '/cloudtrail'
    : services.includes('securityhub')
      ? '/securityhub'
      : services.includes('stacksets')
        ? '/stacksets'
        : '/';

  // CloudKeeper Org Services authenticated routes
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastContainer position="top-right" autoClose={4000} />
      <Router basename="/services">
        <PortalLayout user={portalUser}>
          <Routes>
            <Route path="/" element={<Navigate to={landing} replace />} />
            <Route
              path="/cloudtrail"
              element={<RequireService name="cloudtrail"><TrailList /></RequireService>}
            />
            <Route
              path="/cloudtrail/trails/create"
              element={<RequireService name="cloudtrail"><CreateTrail /></RequireService>}
            />
            <Route
              path="/cloudtrail/trails/:trailId"
              element={<RequireService name="cloudtrail"><TrailDetail /></RequireService>}
            />
            <Route
              path="/cloudtrail/trails/:trailId/edit"
              element={<RequireService name="cloudtrail"><EditTrail /></RequireService>}
            />
            {/* Security Hub umbrella routes. */}
            <Route
              path="/securityhub"
              element={<RequireService name="securityhub"><Overview /></RequireService>}
            />
            <Route
              path="/securityhub/configuration"
              element={<RequireService name="securityhub"><Configuration /></RequireService>}
            />
            <Route
              path="/securityhub/config"
              element={<RequireService name="securityhub"><ConfigRecorders /></RequireService>}
            />
            {/* CSPM detection-engine routes, re-homed under /securityhub/cspm. */}
            <Route
              path="/securityhub/cspm"
              element={<RequireService name="securityhub"><SecurityHubEntry /></RequireService>}
            />
            <Route
              path="/securityhub/cspm/enrollment"
              element={<RequireService name="securityhub"><EnrollmentPage /></RequireService>}
            />
            <Route
              path="/securityhub/cspm/settings"
              element={<RequireService name="securityhub"><SettingsPage /></RequireService>}
            />
            <Route
              path="/securityhub/cspm/standards"
              element={<RequireService name="securityhub"><StandardsList /></RequireService>}
            />
            <Route
              path="/securityhub/cspm/standards/:standardId"
              element={<RequireService name="securityhub"><StandardDetail /></RequireService>}
            />
            <Route
              path="/securityhub/cspm/standards/:standardId/controls/:controlId"
              element={<RequireService name="securityhub"><ControlDetail /></RequireService>}
            />
            <Route
              path="/securityhub/cspm/findings"
              element={<RequireService name="securityhub"><CspmFindings /></RequireService>}
            />
            <Route
              path="/securityhub/config-rules"
              element={<RequireService name="securityhub"><ConfigRules /></RequireService>}
            />
            {/* GuardDuty detection-engine routes, under /securityhub/guardduty. */}
            <Route
              path="/securityhub/guardduty"
              element={<RequireService name="securityhub"><GuardDutyEntry /></RequireService>}
            />
            <Route
              path="/securityhub/guardduty/findings"
              element={<RequireService name="securityhub"><GuardDutyFindings /></RequireService>}
            />
            <Route
              path="/securityhub/guardduty/enrollment"
              element={<RequireService name="securityhub"><GuardDutyEnrollmentPage /></RequireService>}
            />
            <Route
              path="/securityhub/guardduty/settings"
              element={<RequireService name="securityhub"><GuardDutySettingsPage /></RequireService>}
            />
            {/* Amazon Macie detection-engine routes, under /securityhub/macie.
                MacieEntry renders the dashboard / setup / in-progress views
                internally, mirroring GuardDutyEntry. */}
            <Route
              path="/securityhub/macie"
              element={<RequireService name="securityhub"><MacieEntry /></RequireService>}
            />
            <Route
              path="/securityhub/macie/buckets"
              element={<RequireService name="securityhub"><MacieBuckets /></RequireService>}
            />
            <Route
              path="/securityhub/macie/findings"
              element={<RequireService name="securityhub"><MacieFindings /></RequireService>}
            />
            <Route
              path="/securityhub/macie/enrollment"
              element={<RequireService name="securityhub"><MacieEnrollmentPage /></RequireService>}
            />
            <Route
              path="/securityhub/macie/settings"
              element={<RequireService name="securityhub"><MacieSettingsPage /></RequireService>}
            />
            {/* Amazon Inspector detection-engine routes, under /securityhub/inspector.
                One route per nav[] entry in engines/inspector.js — routes are not
                generated from the descriptor, so a missing one would fall through
                to the catch-all redirect instead of 404-ing visibly. InspectorEntry
                (not InspectorDashboard) owns this path: it picks between the setup
                wizard, the in-progress view and the dashboard from config state. */}
            <Route
              path="/securityhub/inspector"
              element={<RequireService name="securityhub"><InspectorEntry /></RequireService>}
            />
            <Route
              path="/securityhub/inspector/findings"
              element={<RequireService name="securityhub"><InspectorFindings /></RequireService>}
            />
            <Route
              path="/securityhub/inspector/enrollment"
              element={<RequireService name="securityhub"><InspectorEnrollmentPage /></RequireService>}
            />
            <Route
              path="/securityhub/inspector/settings"
              element={<RequireService name="securityhub"><InspectorSettingsPage /></RequireService>}
            />
            {/* Amazon Detective detection-engine routes, under /securityhub/detective.
                One route per nav[] entry in engines/detective.js — routes are not
                generated from the descriptor, so a missing one would fall through
                to the catch-all redirect instead of 404-ing visibly. DetectiveEntry
                (not DetectiveDashboard) owns this path: it picks between the setup
                wizard, the in-progress view and the dashboard from config state.

                There is no route guard for DD4 (a deployment that cannot offer
                Detective at all): the sidebar simply omits the rows, and the
                EnableCapabilities tile carries the explanation. A hand-typed URL
                still lands on DetectiveEntry, which surfaces the backend's own
                unavailable response rather than a blank page. */}
            <Route
              path="/securityhub/detective"
              element={<RequireService name="securityhub"><DetectiveEntry /></RequireService>}
            />
            <Route
              path="/securityhub/detective/findings"
              element={<RequireService name="securityhub"><DetectiveFindings /></RequireService>}
            />
            <Route
              path="/securityhub/detective/enrollment"
              element={<RequireService name="securityhub"><DetectiveEnrollmentPage /></RequireService>}
            />
            <Route
              path="/securityhub/detective/settings"
              element={<RequireService name="securityhub"><DetectiveSettingsPage /></RequireService>}
            />
            {/* AWS IAM Access Analyzer detection-engine routes, under
                /securityhub/accessanalyzer. One route per nav[] entry in
                engines/accessanalyzer.js plus its enableRoute (which is the same
                path as the Dashboard entry) - routes are NOT generated from the
                descriptor, so a missing one falls through to the catch-all redirect
                and looks like the page does not exist rather than 404-ing visibly.
                AccessAnalyzerEntry (not AccessAnalyzerDashboard) owns this path: it
                picks between the setup wizard, the in-progress view and the
                dashboard from config state. */}
            <Route
              path="/securityhub/accessanalyzer"
              element={<RequireService name="securityhub"><AccessAnalyzerEntry /></RequireService>}
            />
            <Route
              path="/securityhub/accessanalyzer/findings"
              element={<RequireService name="securityhub"><AccessAnalyzerFindings /></RequireService>}
            />
            <Route
              path="/securityhub/accessanalyzer/enrollment"
              element={<RequireService name="securityhub"><AccessAnalyzerEnrollmentPage /></RequireService>}
            />
            <Route
              path="/securityhub/accessanalyzer/settings"
              element={<RequireService name="securityhub"><AccessAnalyzerSettingsPage /></RequireService>}
            />
            {/* CloudFormation StackSets routes. Static /create and /:id/edit are
                registered before /:id (React Router v6 ranks static over dynamic
                anyway, but keep the explicit order for readability). */}
            <Route
              path="/stacksets"
              element={<RequireService name="stacksets"><StackSetsEntry /></RequireService>}
            />
            <Route
              path="/stacksets/create"
              element={<RequireService name="stacksets"><StackSetCreate /></RequireService>}
            />
            <Route
              path="/stacksets/:id/edit"
              element={<RequireService name="stacksets"><StackSetEdit /></RequireService>}
            />
            <Route
              path="/stacksets/:id"
              element={<RequireService name="stacksets"><StackSetDetail /></RequireService>}
            />
            <Route path="*" element={<Navigate to={landing} replace />} />
          </Routes>
        </PortalLayout>
      </Router>
    </ThemeProvider>
  );
}

// Main SSO Admin App Component
function SSOAdminApp() {
  // Authentication state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authLoading, setAuthLoading] = useState(true);
  const [authError, setAuthError] = useState(null);

  // Separate application admin and customer admin states
  const [isAppAdmin, setIsAppAdmin] = useState(false);
  const [isCustomerAdmin, setIsCustomerAdmin] = useState(false);

  // Add MFA setup requirement state
  // const [requiresMFASetup, setRequiresMFASetup] = useState(false);

  // Add a data loaded state to track initial loading
  const [dataLoaded, setDataLoaded] = useState({
    users: false,
    groups: false,
    permissionSets: false,
    policies: false,
    awsAccounts: false,
    permissionSetAssignments: false,
    replicationConfigs: false
  });

  const {
    users,
    usersTotal,
    groups,
    groupsTotal,
    groupMembers,
    permissionSets,
    awsManagedPolicies,
    awsAccounts,
    permissionSetAssignments,
    ssoConfiguration,
    userReplicationResult,
    accountOnboardingResult,
    fullReplicationResult,
    loading,
    error,
    success,
    fetchAllUsers,
    fetchAllGroups,
    fetchGroupMembers,
    createUser,
    createGroup,
    deleteGroup,
    getUser,
    getGroup,
    updateUser,
    deleteUser,
    assignUsersToGroup,
    removeUsersFromGroup,
    // Permission set methods
    fetchAllPermissionSets,
    getPermissionSet,
    createPermissionSet,
    updatePermissionSet,
    deletePermissionSet,
    // AWS Managed Policy methods
    fetchAwsManagedPolicies,
    // New methods
    fetchAwsAccounts,
    fetchPermissionSetAssignments,
    getPermissionSetAssignment,
    createPermissionSetAssignment,
    deletePermissionSetAssignment,
    // Replication methods
    fetchSsoConfiguration,
    replicateUsersToKeycloak,
    onboardAWSAccounts,
    replicateFullConfiguration,
    ssoFetchProgress,
    replicationProgress,
    // AWS Account deboard methods
    deboardAWSAccount,
  } = useApi();

  // Fix 1: Add loading state to prevent showing spinner unnecessarily
  const [appInitializing, setAppInitializing] = useState(true);

  // Add mobile detection state
  const [isMobile, setIsMobile] = useState(false);
  const [deviceType, setDeviceType] = useState('desktop');

  // Add application admin authentication state
  const [appAdminLoading, setAppAdminLoading] = useState(true);

  // Check authentication on app start
  useEffect(() => {
    checkAuthenticationStatus();

    // Listen for auth errors from API service
    const handleAuthError = (event) => {
      setAuthError(event.detail.message);
      setIsAuthenticated(false);
      setAuthLoading(false);
    };

    window.addEventListener('auth-error', handleAuthError);
    return () => window.removeEventListener('auth-error', handleAuthError);
  }, []);

  const checkAuthenticationStatus = async () => {
    setAuthLoading(true);
    setAuthError(null);

    // Check for impersonation cookie (set by app admin portal on parent domain)
    try {
      const cookies = document.cookie.split(';').map(c => c.trim());
      const impersonateCookie = cookies.find(c => c.startsWith('impersonate_session='));
      if (impersonateCookie) {
        const cookieValue = decodeURIComponent(impersonateCookie.split('=').slice(1).join('='));
        const impersonationData = JSON.parse(cookieValue);

        // Store tokens via apiService so all internal state is consistent
        apiService.setAuthToken(impersonationData.authToken, impersonationData.tokenExpiry);
        if (impersonationData.refreshToken) {
          localStorage.setItem('refreshToken', impersonationData.refreshToken);
          apiService.refreshToken = impersonationData.refreshToken;
        }
        localStorage.setItem('adminDetails', JSON.stringify(impersonationData.adminDetails));
        localStorage.setItem('impersonating', JSON.stringify(impersonationData.impersonating));

        // Clear the cookie immediately
        const currentHost = window.location.hostname;
        const parts = currentHost.split('.');
        const parentDomain = '.' + parts.slice(1).join('.');
        document.cookie = `impersonate_session=; domain=${parentDomain}; path=/; max-age=0; secure; samesite=lax`;
      }
    } catch (e) {
      console.warn('Failed to process impersonation cookie:', e);
    }

    try {
      // Check if we're on customer subdomain
      const isCustomerContext = isCustomerSubdomain();

      if (isCustomerContext) {
        // Customer admin authentication check
        if (apiService.isTokenValid()) {
          setIsCustomerAdmin(true);
          setIsAuthenticated(true);
          setAuthLoading(false);
          return;
        }

        // Try to check customer auth status
        const response = await apiService.checkAuthStatus();
        if (response.success) {
          setIsCustomerAdmin(false);
          setIsAuthenticated(false);
        }
      } else {
        // Application admin authentication check
        if (apiService.isAppAdminTokenValid()) {
          setIsAppAdmin(true);
          setIsAuthenticated(true);
          setAuthLoading(false);
          return;
        }

        // Try to check app admin status
        const response = await apiService.checkAppAdminStatus();
        if (response.success) {
          setIsAppAdmin(false);
          setIsAuthenticated(false);
        }
      }
    } catch (err) {
      console.error('Auth check failed:', err);
      setIsAuthenticated(false);
    } finally {
      setAuthLoading(false);
    }
  };

  const checkAndInitializeKeycloak = async () => {
    try {
      // First check if Keycloak is already initialized
      const statusResponse = await apiService.getKeycloakStatus();

      if (statusResponse.success && statusResponse.data.initialized) {
        // Already initialized, no action needed
        return;
      }

      // Not initialized, attempt to initialize silently
      await apiService.initializeKeycloak();
      // Removed console logs - keep initialization completely silent
    } catch (keycloakError) {
      console.error('Keycloak initialization failed:', keycloakError);
      // Don't show error toast as this is a background process
      // The user can retry from the Init page if needed
    }
  };

  const handleLogin = (loginData) => {
    setIsAuthenticated(true);
    setAuthError(null);
    console.log('Login successful:', loginData);

    // Determine if this is app admin or customer admin login
    if (isCustomerSubdomain()) {
      setIsCustomerAdmin(true);
      // Store customer admin details
      const adminDetails = {
        username: loginData.username,
        firstName: loginData.firstName || '',
        role: loginData.role,
        isAdmin: loginData.isAdmin,
        customerId: loginData.customerId
      };
      localStorage.setItem('adminDetails', JSON.stringify(adminDetails));
    } else {
      setIsAppAdmin(true);
      // Store app admin details
      const appAdminDetails = {
        username: loginData.username,
        isAppAdmin: true
      };
      localStorage.setItem('appAdminDetails', JSON.stringify(appAdminDetails));
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setAuthError(null);

    // Clear appropriate tokens and state based on context
    if (isCustomerSubdomain()) {
      setIsCustomerAdmin(false);
      localStorage.removeItem('adminDetails');
    } else {
      setIsAppAdmin(false);
      localStorage.removeItem('appAdminDetails');
    }

    // Clear data loaded state
    setDataLoaded({
      users: false,
      groups: false,
      permissionSets: false,
      policies: false,
      awsAccounts: false,
      permissionSetAssignments: false,
      replicationConfigs: false
    });
  };

  // New handlers for application admin login/logout
  const handleAppAdminLogin = (loginData) => {
    setIsAppAdmin(true);
    setIsAuthenticated(true);
    localStorage.setItem('adminToken', loginData.token);
  };

  // const handleAppAdminLogout = () => {
  //   setIsAppAdmin(false);
  //   setIsAuthenticated(false);
  //   localStorage.removeItem('adminToken');
  //   localStorage.removeItem('adminDetails');
  // };

  // Create memoized version of fetchAwsManagedPolicies for components to use
  const fetchPoliciesIfNeeded = useCallback(() => {
    // Only fetch if not already loaded
    if (!dataLoaded.policies) {
      setDataLoaded(prev => ({ ...prev, policies: true }));
      return fetchAwsManagedPolicies();
    }
    return Promise.resolve(awsManagedPolicies);
  }, [fetchAwsManagedPolicies, dataLoaded.policies, awsManagedPolicies]);

  // Memoize other fetch functions too
  const fetchUsersIfNeeded = useCallback(() => {
    if (!dataLoaded.users) {
      setDataLoaded(prev => ({ ...prev, users: true }));
      return fetchAllUsers();
    }
    return Promise.resolve(users);
  }, [fetchAllUsers, dataLoaded.users, users]);

  const fetchGroupsIfNeeded = useCallback(() => {
    if (!dataLoaded.groups) {
      setDataLoaded(prev => ({ ...prev, groups: true }));
      return fetchAllGroups();
    }
    return Promise.resolve(groups);
  }, [fetchAllGroups, dataLoaded.groups, groups]);

  const fetchPermissionSetsIfNeeded = useCallback(() => {
    if (!dataLoaded.permissionSets) {
      setDataLoaded(prev => ({ ...prev, permissionSets: true }));
      return fetchAllPermissionSets();
    }
    return Promise.resolve(permissionSets);
  }, [fetchAllPermissionSets, dataLoaded.permissionSets, permissionSets]);

  // New memoized function for AWS accounts
  const fetchAccountsIfNeeded = useCallback(() => {
    if (!dataLoaded.awsAccounts) {
      setDataLoaded(prev => ({ ...prev, awsAccounts: true }));
      return fetchAwsAccounts();
    }
    return Promise.resolve(awsAccounts);
  }, [fetchAwsAccounts, dataLoaded.awsAccounts, awsAccounts]);

  // New memoized function for permission set assignments
  const fetchPermissionSetAssignmentsIfNeeded = useCallback(() => {
    if (!dataLoaded.permissionSetAssignments) {
      setDataLoaded(prev => ({ ...prev, permissionSetAssignments: true }));
      return fetchPermissionSetAssignments();
    }
    return Promise.resolve(permissionSetAssignments);
  }, [fetchPermissionSetAssignments, dataLoaded.permissionSetAssignments, permissionSetAssignments]);

  // Update memoized function for SSO configuration
  const fetchSsoConfigurationIfNeeded = useCallback(() => {
    if (!dataLoaded.replicationConfigs) {
      setDataLoaded(prev => ({ ...prev, replicationConfigs: true }));
      return fetchSsoConfiguration();
    }
    return Promise.resolve(ssoConfiguration);
  }, [fetchSsoConfiguration, dataLoaded.replicationConfigs, ssoConfiguration]);

  // Show toasts for error and success messages
  useEffect(() => {
    if (error) {
      toast.error(error);
    }
  }, [error]);

  useEffect(() => {
    if (success) {
      toast.success(success);
    }
  }, [success]);

  useEffect(() => {
    // Set initializing to false after a delay to avoid flashing loading spinner
    const initTimer = setTimeout(() => {
      setAppInitializing(false);
    }, 1000);

    return () => clearTimeout(initTimer);
  }, []);

  // Check for mobile device on mount and resize
  useEffect(() => {
    const checkDevice = () => {
      const mobile = isMobileDevice();
      const device = getDeviceType();
      setIsMobile(mobile);
      setDeviceType(device);
    };

    // Initial check
    checkDevice();

    // Listen for window resize to handle device rotation/responsive changes
    const handleResize = () => {
      setTimeout(checkDevice, 100); // Small delay to ensure dimensions are updated
    };

    window.addEventListener('resize', handleResize);
    window.addEventListener('orientationchange', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('orientationchange', handleResize);
    };
  }, []);

  // Check application admin authentication on app start
  useEffect(() => {
    checkAppAdminAuth();
  }, []);

  const checkAppAdminAuth = async () => {
    setAppAdminLoading(true);
    try {
      const token = localStorage.getItem('adminToken');
      if (token) {
        // Validate token with backend
        const response = await apiService.getCurrentAdmin();
        if (response.success) {
          setIsAppAdmin(true);
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminDetails');
        }
      }
    } catch (err) {
      console.error('App admin auth check failed:', err);
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminDetails');
    } finally {
      setAppAdminLoading(false);
      setAppInitializing(false);
    }
  };

  // Show loading spinner while checking authentication
  if (authLoading || appInitializing || appAdminLoading) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Box sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          background: 'linear-gradient(180deg, rgba(13, 17, 23, 0.98), rgba(22, 27, 34, 0.95))'
        }}>
          <LoadingSpinner />
        </Box>
      </ThemeProvider>
    );
  }

  // SSO Callback route (bypass authentication)
  if (window.location.pathname === '/sso-callback') {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <Router>
          <Routes>
            <Route path="/sso-callback" element={<SSOCallback onLogin={handleLogin} />} />
          </Routes>
        </Router>
      </ThemeProvider>
    );
  }

  // Check subdomain context and show appropriate login
  const isCustomerContext = isCustomerSubdomain();
  const currentPath = window.location.pathname;

  // Show appropriate login based on subdomain
  if (!isAuthenticated) {
    if (isCustomerContext) {
      // /init path on customer subdomain - show Customer admin login
      if (currentPath === '/init') {
        // Block mobile access for /init login page (portal will open directly after login)
        if (isMobile) {
          return (
            <ThemeProvider theme={theme}>
              <CssBaseline />
              <DesktopOnlyMessage />
            </ThemeProvider>
          );
        }
        return (
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <LoginPage onLogin={handleLogin} onAuthStatusChange={() => { }} />
          </ThemeProvider>
        );
      }

      // Any other path on customer subdomain without auth - redirect to Keycloak ckauth-application
      // Allow this redirect on mobile so users can reach the login page
      const ckAuthAppURL = getCkAuthApplicationURL();
      if (ckAuthAppURL) {
        window.location.href = ckAuthAppURL;
        // Show loading while redirecting
        return (
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Box sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100vh',
              background: 'linear-gradient(180deg, rgba(13, 17, 23, 0.98), rgba(22, 27, 34, 0.95))'
            }}>
              <LoadingSpinner />
            </Box>
          </ThemeProvider>
        );
      }
    } else {
      // Application admin login (no subdomain - main domain)
      // Block mobile access for app admin login
      if (isMobile) {
        return (
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <DesktopOnlyMessage />
          </ThemeProvider>
        );
      }
      return (
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <ApplicationAdminLogin onLogin={handleLogin} />
        </ThemeProvider>
      );
    }
  }

  // Show mobile message if authenticated but on mobile device
  // (block admin portal access on mobile once logged in)
  if (isMobile) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <DesktopOnlyMessage />
      </ThemeProvider>
    );
  }

  // Main application routes
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ToastContainer
        position="top-right"
        autoClose={4000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <Router>
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
          <Sidebar onLogout={handleLogout} />

          <Box
            component="main"
            sx={{
              flexGrow: 1,
              // Without this, wide content (e.g. the Admin Management tables) forces the
              // flex item past the viewport and the whole page scrolls horizontally.
              minWidth: 0,
              display: 'flex',
              flexDirection: 'column',
              position: 'relative',
            }}
          >
            {/* Dynamic Banner - shows at the top of main content area */}
            {isAuthenticated && (
              <Box sx={{
                position: 'sticky',
                top: 0,
                zIndex: 1200, // Below sidebar but above content
                width: '100%'
              }}>
                <ReadOnlyBanner />
                <DynamicBanner />
              </Box>
            )}

            {/* Page content */}
            <Box sx={{ flexGrow: 1 }}>
              <Routes>
                <Route path="/" element={
                  isAppAdmin ? <Navigate to="/customers" replace /> : <Navigate to="/dashboard" replace />
                } />
                {/* /init path - redirect authenticated users to dashboard/customers */}
                <Route path="/init" element={
                  isAppAdmin ? <Navigate to="/customers" replace /> : <Navigate to="/dashboard" replace />
                } />

                {/* Application Admin Only Routes */}
                {isAppAdmin ? (
                  <>
                    <Route path="/customers" element={<CustomerManagement />} />
                    <Route path="/customers/:customerId/dashboard" element={<CustomerDashboard />} />
                    <Route path="/customers/:customerId/login" element={<CustomerLogin />} />
                    {/* Analytics - SaaS mode only */}
                    {(window.env?.DEPLOYMENT_MODE || 'self-hosted') === 'saas' && (
                      <Route path="/analytics" element={<AppAdminAnalytics />} />
                    )}
                    {/* Redirect settings to customers */}
                    <Route path="/settings" element={<Navigate to="/customers" replace />} />
                    {/* Redirect any other routes to customers for app admin */}
                    <Route path="/dashboard" element={<Navigate to="/customers" replace />} />
                    <Route path="/users" element={<Navigate to="/customers" replace />} />
                    <Route path="/groups" element={<Navigate to="/customers" replace />} />
                    <Route path="/permission-sets" element={<Navigate to="/customers" replace />} />
                    <Route path="/permission-set-assignments" element={<Navigate to="/customers" replace />} />
                    <Route path="/replication" element={<Navigate to="/customers" replace />} />
                    <Route path="/accounts" element={<Navigate to="/customers" replace />} />
                    <Route path="/admin-management" element={<Navigate to="/customers" replace />} />
                  </>
                ) : (
                  <>
                    {/* Customer Admin Routes - only show if not app admin */}
                    <Route path="/dashboard" element={
                      <Dashboard
                        users={users}
                        usersTotal={usersTotal}
                        groups={groups}
                        groupsTotal={groupsTotal}
                        permissionSets={permissionSets}
                        awsAccounts={awsAccounts}
                        permissionSetAssignments={permissionSetAssignments}
                        fetchAllUsers={fetchAllUsers}
                        fetchAllGroups={fetchAllGroups}
                        fetchAllPermissionSets={fetchAllPermissionSets}
                        fetchAwsAccounts={fetchAwsAccounts}
                        fetchPermissionSetAssignments={fetchPermissionSetAssignments}
                      />
                    } />

                    {isCustomerAdmin && (
                      <>
                        <Route path="/users" element={
                          <Users
                            users={users}
                            groups={groups}
                            fetchAllUsers={fetchAllUsers}
                            fetchAllGroups={fetchAllGroups}
                            getUser={getUser}
                            createUser={createUser}
                            updateUser={updateUser}
                            deleteUser={deleteUser}
                            fetchGroupMembers={fetchGroupMembers}
                          />
                        } />
                        <Route path="/groups" element={
                          <Groups
                            users={users}
                            groups={groups}
                            groupMembers={groupMembers}
                            fetchAllUsers={fetchAllUsers}
                            fetchAllGroups={fetchAllGroups}
                            fetchGroupMembers={fetchGroupMembers}
                            getGroup={getGroup}
                            createGroup={createGroup}
                            deleteGroup={deleteGroup}
                            assignUsersToGroup={assignUsersToGroup}
                            removeUsersFromGroup={removeUsersFromGroup}
                          />
                        } />
                        <Route path="/permission-sets" element={
                          <PermissionSets
                            permissionSets={permissionSets}
                            awsManagedPolicies={awsManagedPolicies}
                            fetchAllPermissionSets={fetchAllPermissionSets}
                            getPermissionSet={getPermissionSet}
                            createPermissionSet={createPermissionSet}
                            updatePermissionSet={updatePermissionSet}
                            deletePermissionSet={deletePermissionSet}
                            fetchAwsManagedPolicies={fetchAwsManagedPolicies}
                          />
                        } />
                        <Route path="/customer-managed-policies" element={<CustomerManagedPolicies />} />
                        <Route path="/permission-set-assignments" element={
                          <PermissionSetAssignments
                            users={users}
                            groups={groups}
                            permissionSets={permissionSets}
                            permissionSetAssignments={permissionSetAssignments}
                            awsAccounts={awsAccounts}
                            fetchAllUsers={fetchAllUsers}
                            fetchAllGroups={fetchAllGroups}
                            fetchAllPermissionSets={fetchAllPermissionSets}
                            fetchPermissionSetAssignments={fetchPermissionSetAssignments}
                            fetchAwsAccounts={fetchAwsAccounts}
                            getPermissionSetAssignment={getPermissionSetAssignment}
                            createPermissionSetAssignment={createPermissionSetAssignment}
                            deletePermissionSetAssignment={deletePermissionSetAssignment}
                          />
                        } />
                        <Route path="/replication" element={
                          <Replication
                            ssoConfiguration={ssoConfiguration}
                            fetchSsoConfiguration={fetchSsoConfiguration}
                            replicateUsersToKeycloak={replicateUsersToKeycloak}
                            onboardAWSAccounts={onboardAWSAccounts}
                            replicateFullConfiguration={replicateFullConfiguration}
                            userReplicationResult={userReplicationResult}
                            accountOnboardingResult={accountOnboardingResult}
                            fullReplicationResult={fullReplicationResult}
                            ssoFetchProgress={ssoFetchProgress}
                            replicationProgress={replicationProgress}
                          />
                        } />
                        <Route path="/accounts" element={<ManualOnboarding />} />
                        <Route path="/admin-management" element={<AdminManagement />} />
                        <Route path="/identity-providers" element={<IdentityProviders />} />
                        <Route path="/custom-applications" element={<CustomApplicationsPage />} />
                        <Route path="/scim-configuration" element={
                          <SCIMConfiguration />
                        } />
                        {/* Preferences pages for customer admins */}
                        <Route path="/api-tokens" element={<APITokens />} />
                        {/* Logs pages for customer admins */}
                        <Route path="/audit-logs" element={<AuditLogs />} />
                        <Route path="/access-logs" element={<AccessLogs />} />
                        {/* Log Export Settings for customer admins */}
                        <Route path="/log-export-settings" element={<LogExportSettings />} />
                        {/* CloudKeeper SaaS Connection for self-hosted instances */}
                        <Route path="/saas-connection" element={<SaaSConnection />} />
                        {/* Account Contacts Management */}
                        <Route path="/accounts/:accountId/contacts" element={<AccountContactDetail />} />
                        {/* Org Services */}
                        <Route path="/org-services" element={<OrgServices />} />
                      </>
                    )}

                    {/* Redirect settings to identity-providers */}
                    <Route path="/settings" element={<Navigate to="/identity-providers" replace />} />
                  </>
                )}

                {/* Catch all */}
                <Route path="*" element={
                  isAppAdmin ? <Navigate to="/customers" replace /> : <Navigate to="/dashboard" replace />
                } />
              </Routes>
            </Box>
          </Box>
        </Box>
      </Router>
    </ThemeProvider>
  );
}

// App wrapper that decides which portal to render
function App() {
  // Check if we're on JIT Portal subdomain - this is a simple conditional render
  // that doesn't use hooks, so it's safe to do before any hooks
  if (isJITPortal()) {
    document.title = 'CK Prism - JIT Access Portal';
    return (
      <ConfirmDialogProvider>
        <JITPortalApp />
      </ConfirmDialogProvider>
    );
  }

  // Check if we're on the CloudKeeper Org Services portal path
  if (isOrgServicesPortal()) {
    document.title = 'CK Prism - Organization Services';
    return (
      <ConfirmDialogProvider>
        <OrgServicesPortalApp />
      </ConfirmDialogProvider>
    );
  }

  return (
    <ConfirmDialogProvider>
      <SSOAdminApp />
    </ConfirmDialogProvider>
  );
}

export default App;